import { Injectable, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription, lastValueFrom, map, pairwise, startWith } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { LocalStorageService } from 'src/app/common/services/local-storage.service';
import { USER_GROUPS } from 'src/app/common/constant/constant';
import { QuotationService } from './quotation.service';
import { ProposalService } from './proposal.service';
import { PolicyService } from './policy.service';
import { WizardService } from './wizard.service';
import * as pricesActions from '../stores/prices.actions';
import * as quoteActions from '../stores/quote.actions';
import * as proposalActions from '../stores/proposal.actions';

@Injectable({
  providedIn: 'root',
})
export class DataManagerService implements OnDestroy {
  private quote;

  private groupsStateSubscription: Subscription;

  private userGroups: string[] = [];

  constructor(
    private localStorageService: LocalStorageService,
    private quotationService: QuotationService,
    private proposalService: ProposalService,
    private policyService: PolicyService,
    private wizardService: WizardService,
    private store: Store<AppState>
  ) {
    this.getQuote();

    this.groupsStateSubscription = this.store.select('userGroups').subscribe((groups) => {
      this.userGroups = groups;
    });
  }

  private getQuote() {
    this.store.select('quote').subscribe((state) => {
      this.quote = state;
    });
  }

  public setQuote(quote = {}) {
    this.store.dispatch(quoteActions.setQuote({ quote }));
    this.localStorageService.set('currentQuote', quote);
  }

  public setPrices(prices = {}) {
    this.store.dispatch(pricesActions.setPrices({ prices }));
    this.localStorageService.set('quotePrices', prices);
  }

  public setProposal(proposal = {}) {
    this.store.dispatch(proposalActions.setProposal({ proposal }));
    this.localStorageService.set('currentProposal', proposal);
  }

  public async saveQuote(withOutSetQuote = false, data = {}) {
    if (!withOutSetQuote) {
      const { result } = await lastValueFrom(this.quotationService.save());
      this.setQuote(result);
    } else {
      const { result } = await lastValueFrom(this.quotationService.saveWithData(data));
      this.setQuote(result);
    }
  }

  public async updateQuote(data = {}, id = '') {
    const { result } = await lastValueFrom(this.quotationService.update(data, id));
    this.setQuote(result);
  }

  public async calculatePrices(data = {}, id = '') {
    const { result } = await lastValueFrom(this.quotationService.calculatePrices(data, id));
    this.setPrices(result);
  }

  public async verifyClient(data : any) {
    const body = {
      persons : [
        {
          documentType: "CNPJ",
          documentValue: data?.documentNumber,
          businessName: data?.businessName
        }
      ]
    }

    const { persons } = await lastValueFrom(this.quotationService.verifyClient(body));
    let dataUnderwriting = { isUnderwritingApproved: false, underwritingReason : "ES"};
    if(persons.length !== undefined && persons.length > 0){
      if(persons[0].status === "ACEITA"){
        dataUnderwriting = { isUnderwritingApproved: true, underwritingReason : ""};
      }
      if(persons[0].status === "REJEITA"){
        dataUnderwriting = { isUnderwritingApproved: false, underwritingReason : "RE"};
      }

      if(persons[0].status === "REINTENTO"){
        dataUnderwriting = { isUnderwritingApproved: false, underwritingReason : "ES"};
      }
    }

    const updatedData = { ...data, underwriting: dataUnderwriting };
    this.setQuote(updatedData);
  }

  public async queryProperties(id: string, data = {}) {
    const { result } = await lastValueFrom(this.quotationService.queryProperties(data, id));
    this.setQuote(result);
  }

  public async saveProposal(data) {
    const { result } = await lastValueFrom(this.proposalService.save(data));
    this.setQuote(result);
  }

  public async updateProposal(data = {}, id = '') {
    const { result } = await lastValueFrom(this.proposalService.update(data, id));
    this.setProposal(result);
  }

  public async savePolicy(data = {}) {
    const { result } = await lastValueFrom(this.policyService.save(data));
    this.setQuote(result);
  }

  public async validateForm(data) {
    this.getQuote();

    if (!this.quote?.id) await this.saveQuote();
    else await this.updateQuote(data, this.quote.id);
  }

  public validateControlChanged(form, isGroupsArray = false, arrayName = null) {
    this.getQuote();

    let localForm = form as FormGroup;

    if (isGroupsArray) {
      localForm = form.get(arrayName) as FormGroup;
    }

    localForm.valueChanges
      .pipe(
        startWith(localForm.value),
        pairwise(),
        map(([prev, curr]) =>
          Object.keys(curr).find((key) => {
            if (Array.isArray(prev[key]) || Array.isArray(curr[key])) {
              return (
                prev[key]?.length !== curr[key]?.length ||
                prev[key]?.every((value, index) => value !== curr[key][index])
              );
            }

            return prev[key] !== curr[key];
          })
        )
      )
      .subscribe((key) => {
        if (key) {
          const control = localForm.controls[key];

          if (control.valid) {
            if (isGroupsArray) {
              let groupsArray = [];
              if (Object.prototype.hasOwnProperty.call(this.quote, arrayName)) {
                groupsArray = [...this.quote[arrayName]];
              }

              groupsArray[key] = control.value;

              const quote = { ...this.quote, [arrayName]: groupsArray };

              this.setQuote(quote);
            } else {
              const quote = { ...this.quote, [key]: control.value };
              this.setQuote(quote);
            }
          }
        }
      });
  }

  public addToPrevious() {
    this.store.dispatch(quoteActions.addToPrevious());
  }

  public setFromPreviousQuotes(): void {
    this.localStorageService.set('fromPreviousQuotes', true);
  }

  public getFromPreviousQuotes(): boolean {
    return this.localStorageService.get('fromPreviousQuotes');
  }

  public removeFromPreviousQuotes(): void {
    this.localStorageService.remove('fromPreviousQuotes');
  }

  public removeStorages(): void {
    this.localStorageService.remove('fromPreviousQuotes');
    this.localStorageService.remove('currentQuote');
    this.localStorageService.remove('quotePrices');
    this.localStorageService.remove('steps');
    this.wizardService.resetWizardCurrentStep();
    this.setQuote();
  }

  public updateStepForm(quoteForm: FormGroup): void {
    if (this.quote) quoteForm.patchValue(this.quote);
  }

  public getDataFromStep(quoteForm: FormGroup) {
    return {
      ...this.quote,
      ...quoteForm.getRawValue(),
    };
  }

  public getDataFromStore() {
    return {
      ...this.quote,
    };
  }

  public updateStepCurrentCode(code: string): void {
    this.validateForm({
      ...this.quote,
      currentStepCode: code,
    });
  }

  public getControl(name: string) {
    const splitted = name.split('.');
    if (!(splitted[0] in this.quote)) return null;
    const finded = splitted.reduce((o, i) => {
      if (o) return o[i];
      return null;
    }, this.quote);
    return finded !== null && finded !== undefined ? finded : null;
  }

  public removeItemFromArray(arrayName, key) {
    const quote = [...this.quote[arrayName]];
    quote.splice(key, 1);
    this.setQuote({ ...this.quote, [arrayName]: quote });
  }

  public getNumberItemsOfArray(arrayName): number {
    return this.quote[arrayName]?.length || 0;
  }

  public isAdviser() {
    return this.userGroups.includes(USER_GROUPS.ADVISER);
  }

  public isComercial() {
    return this.userGroups.includes(USER_GROUPS.COMERCIAL);
  }

  public isBroker() {
    return this.userGroups.includes(USER_GROUPS.BROKER);
  }

  ngOnDestroy(): void {
    if (this.groupsStateSubscription) this.groupsStateSubscription.unsubscribe();
  }
}
